import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { formInitialize, formSet, formUninitialize, useCpFormSubmit } from "../gvc/testNewForm/utils";
import { useAppDispatch } from "../../store/hooks";
import { formActions } from "../../store/formSlice";
import { Modal, notification } from "antd";
import { linksOf, request } from "../../services/cpln";
import { CpFormPromptDetail } from "../../components/antd/form/CpFormPromptDetail";
import { CpFormContext } from "../../components/form/CpFormContext";
import { CpFormValidator } from "../gvc/testNewForm/CpFormValidator";
import { CpPreviewModal } from "../workload/draft/previewModal";
import { CpDebugModal } from "../workload/draft/debugModal";
import { Location } from "../../schema/types/location/entity";
import { useGetLocationQuery } from "../../store/api/locationApi";
import { inputToLocation, locationToInput, locationToPatch } from "./utils";
import { getValidator } from "./create/validator";
import { LocationInput } from "../../schema/types/location/input";
import { LocationDetailContent } from "./detailContent";
import { getDirtyChecker } from "./create/dirtyChecker";
import { CpFormDirtyChecker } from "../gvc/testNewForm/CpFormDirtyChecker";

interface Props {
  location: Location;
}
export const LocationDetailForm: React.FC<Props> = ({ location }) => {
  const dispatch = useAppDispatch();
  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  const { refetch } = useGetLocationQuery(linksOf(location).self!);

  // form init
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    const input = locationToInput(location);
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  // confirm
  React.useEffect(() => {
    dispatch(formActions.setInitialValues({ id: formId, values: locationToInput(location) }));
  }, [location.version]);

  // validation/dirty/submit
  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;
  const dirtyCheckerRef = React.useRef(getDirtyChecker(formId));
  const dirtyChecker = dirtyCheckerRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit });

  async function handleSubmit(input: LocationInput) {
    try {
      let body = locationToPatch(inputToLocation(input), { id: input.id, version: input.version });
      const method = "patch";
      const url = linksOf(location).self!;

      await request({
        method: method,
        url: url,
        body: body,
      });

      formSet(formId, `meta.applied`, true);

      notification.success({
        message: "Success",
        description: "Location is updated",
      });

      refetch();
    } catch (e) {
      if (e?.response?.status === 409) {
        Modal.warning({
          title: "Update Conflict",
          content: "This resource has been modified since you opened it. The form will be refreshed with the latest data and your changes will be lost.",
          onOk: () => {
            refetch();
          },
        });
        return;
      }

      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit: submit }}>
      <LocationDetailContent location={location} refetch={refetch} />
      <CpFormValidator validationFn={validator} />
      <CpFormDirtyChecker checker={dirtyChecker} />
      <CpFormPromptDetail link={linksOf(location).self!} inputToItemFn={inputToLocation} />
      <CpPreviewModal inputToItemFn={inputToLocation} kind="location" namePath="name" title="Location" />
      <CpDebugModal inputToItemFn={inputToLocation} kind="location" namePath="name" title="Location" />
    </CpFormContext.Provider>
  );
};
